import { Button, Group, Modal, Radio, Stack, Text, Paper, Flex, Box, Checkbox, Divider } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';

interface ExportDialogProps {
  opened: boolean;
  onClose: () => void;
  datasetId: string;
  currentPage: number;
  pageSize: number;
  orderBy?: string;
  selectedIds?: string[];
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  opened,
  onClose,
  datasetId,
  currentPage,
  pageSize,
  orderBy,
  selectedIds = [],
}) => {
  const { t, i18n } = useTranslation();
  const [exportType, setExportType] = useState('current_page');
  const [isExporting, setIsExporting] = useState(false);

  // 可用语言列表
  const availableLanguages = [
    { key: 'zh', label: t('language.chinese') },
    { key: 'en', label: t('language.english') },
    { key: 'ja', label: t('language.japanese') },
    { key: 'ko', label: t('language.korean') },
    { key: 'vi', label: t('language.vietnamese') },
    { key: 'pt', label: t('language.portuguese') },
    { key: 'pt-BR', label: t('language.portuguese_brazil') },
    { key: 'id', label: t('language.indonesian') },
    { key: 'ne', label: t('language.nepali') },
    { key: 'es', label: t('language.spanish') },
    { key: 'my', label: t('language.burmese') },
  ];

  // 选中的语言
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['zh', 'en', 'ja']);

  const handleLanguageChange = (value: string[]) => {
    setSelectedLanguages(value);
  };

  const handleSelectAll = () => {
    setSelectedLanguages(availableLanguages.map((lang) => lang.key));
  };

  const handleUnselectAll = () => {
    setSelectedLanguages([]);
  };

  const handleExport = async () => {
    if (selectedLanguages.length === 0) {
      notifications.show({
        message: t('faqs.export.no_language_selected'),
        color: 'red',
      });
      return;
    }

    // 验证选中项导出
    if (exportType === 'selected' && selectedIds.length === 0) {
      notifications.show({
        message: t('faqs.export.no_items_selected'),
        color: 'red',
      });
      return;
    }

    setIsExporting(true);

    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      export_type: exportType,
      page: currentPage.toString(),
      page_size: pageSize.toString(),
      languages: selectedLanguages.join(','), // 传递多个语言
    });

    // 添加选中的FAQ IDs
    if (exportType === 'selected' && selectedIds.length > 0) {
      params.append('selected_ids', selectedIds.join(','));
    }

    // 添加排序参数
    if (orderBy) {
      params.append('order_by', orderBy);
    }

    const APIBaseURL = window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
    const exportUrl = `${APIBaseURL}/datasets/${datasetId}/faqs/export?${params}`;

    try {
      const response = await fetch(exportUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed with status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = `Q&A_${datasetId}_${exportType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      notifications.show({
        message: t('faqs.action.export_success'),
        color: 'green',
      });

      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      notifications.show({
        message: t('faqs.action.export_failed'),
        color: 'red',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          {t('faqs.export.title')}
        </Text>
      }
      centered
      size="lg"
    >
      <Stack gap="lg">
        <Radio.Group value={exportType} onChange={setExportType}>
          <Stack gap="md">
            {/* 导出已选择的对象 - 放在最上面 */}
            <Paper
              p="md"
              withBorder
              radius="md"
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: exportType === 'selected' ? 'var(--mantine-color-violet-0)' : undefined,
                borderColor: exportType === 'selected' ? 'var(--mantine-color-violet-4)' : undefined,
                opacity: selectedIds.length === 0 ? 0.6 : 1,
              }}
              onClick={() => selectedIds.length > 0 && setExportType('selected')}
            >
              <Flex align="flex-start" gap="md">
                <Box flex={1}>
                  <Radio
                    value="selected"
                    disabled={selectedIds.length === 0}
                    label={
                      <Text fw={500} size="sm">
                        {t('faqs.export.selected')}
                      </Text>
                    }
                    description={t('faqs.export.selected_description', { count: selectedIds.length })}
                  />
                </Box>
              </Flex>
            </Paper>

            <Paper
              p="md"
              withBorder
              radius="md"
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: exportType === 'current_page' ? 'var(--mantine-color-violet-0)' : undefined,
                borderColor: exportType === 'current_page' ? 'var(--mantine-color-violet-4)' : undefined,
              }}
              onClick={() => setExportType('current_page')}
            >
              <Flex align="flex-start" gap="md">
                <Box flex={1}>
                  <Radio
                    value="current_page"
                    label={
                      <Text fw={500} size="sm">
                        {t('faqs.export.current_page')}
                      </Text>
                    }
                    description={t('faqs.export.current_page_description')}
                  />
                </Box>
              </Flex>
            </Paper>

            <Paper
              p="md"
              withBorder
              radius="md"
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: exportType === 'all' ? 'var(--mantine-color-violet-0)' : undefined,
                borderColor: exportType === 'all' ? 'var(--mantine-color-violet-4)' : undefined,
              }}
              onClick={() => setExportType('all')}
            >
              <Flex align="flex-start" gap="md">
                <Box flex={1}>
                  <Radio
                    value="all"
                    label={
                      <Text fw={500} size="sm">
                        {t('faqs.export.all')}
                      </Text>
                    }
                    description={t('faqs.export.all_description')}
                  />
                </Box>
              </Flex>
            </Paper>
          </Stack>
        </Radio.Group>

        <Divider my="xl" />

        {/* 语言选择区域 */}
        <Box>
          <Group justify="space-between" align="center" mb="md">
            <Text size="lg" fw={600}>
              {t('faqs.export.export_language')}
            </Text>
            <Group>
              <Button variant="subtle" size="xs" onClick={handleSelectAll} c="violet">
                {t('faqs.export.select_all')}
              </Button>
              <Button variant="subtle" size="xs" onClick={handleUnselectAll} c="gray">
                {t('faqs.export.unselect_all')}
              </Button>
            </Group>
          </Group>

          <Text size="sm" c="dimmed" mb="lg">
            {t('faqs.export.language_selection_desc')}
          </Text>

          <Paper
            p="lg"
            withBorder
            radius="md"
            style={{
              borderColor: '#e0e0e0',
              backgroundColor: '#fafafa',
            }}
          >
            <Checkbox.Group value={selectedLanguages} onChange={handleLanguageChange}>
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '16px',
                }}
              >
                {availableLanguages.map((language) => (
                  <Checkbox
                    key={language.key}
                    value={language.key}
                    label={language.label}
                    size="md"
                    styles={{
                      input: {
                        '&:checked': {
                          backgroundColor: 'var(--mantine-color-violet-6)',
                          borderColor: 'var(--mantine-color-violet-6)',
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Checkbox.Group>
          </Paper>
        </Box>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={onClose} size="sm">
            {t('alert_cancel')}
          </Button>
          <Button
            loading={isExporting}
            onClick={handleExport}
            leftSection={<IconDownload size={16} />}
            size="sm"
            disabled={selectedLanguages.length === 0}
          >
            {t('alert_ok')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
